import React, { useCallback, useEffect, useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import FormField from '../ui/FormField';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import Callout from '../ui/Callout';
import { useT, useLocale } from '../../i18n/LocaleContext';
import { useToast } from '../../contexts/ToastContext';
import { useData } from '../../data/DataContext';
import { getPilgrimageSchedule, savePilgrimageSchedule } from '../../api/settings';
import { formatCurrency, formatDate } from '../../utils/format';

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function toForm(s) {
  return {
    enabled: Boolean(s.enabled),
    name: s.name || '',
    destination: s.destination || '',
    description: s.description || '',
    price: s.price != null ? String(Math.round(Number(s.price))) : '',
    monthsAhead: s.monthsAhead != null ? String(s.monthsAhead) : '2',
    closeTime: s.closeTime || '00:00',
    departureTime: s.departureTime || '05:00',
  };
}

function formatDateTime(iso, locale) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(locale === 'fr' ? 'fr-FR' : 'en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Kigali',
  });
}

/**
 * Automatic last-Sunday pilgrimage: the saved details (price included) stay
 * in place until changed, and every month's pilgrimage is created from them.
 */
export default function MonthlyPilgrimageCard({ canEdit }) {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const { refreshAll } = useData();
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getPilgrimageSchedule();
      setSaved(data);
      setForm(toForm(data));
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    }
  }, [toast, t]);

  useEffect(() => { load(); }, [load]);

  if (!form) {
    return <Card title={t('monthly.title')} subtitle={t('common.loading')} />;
  }

  const set = (field) => (e) => {
    const value = e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = t('validation.required');
    if (!form.destination.trim()) next.destination = t('validation.required');
    const price = Number(form.price);
    if (!Number.isInteger(price) || price < 100) next.price = t('monthly.price_invalid');
    const months = Number(form.monthsAhead);
    if (!Number.isInteger(months) || months < 0 || months > 6) next.monthsAhead = t('monthly.months_invalid');
    if (!TIME_RE.test(form.closeTime)) next.closeTime = t('monthly.time_invalid');
    if (!TIME_RE.test(form.departureTime)) next.departureTime = t('monthly.time_invalid');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    setSaving(true);
    try {
      const data = await savePilgrimageSchedule({
        enabled: form.enabled,
        name: form.name.trim(),
        destination: form.destination.trim(),
        description: form.description.trim(),
        price: Number(form.price),
        monthsAhead: Number(form.monthsAhead),
        closeTime: form.closeTime,
        departureTime: form.departureTime,
      });
      setSaved(data);
      setForm(toForm(data));
      toast.success(t('monthly.toast_saved'));
      refreshAll?.();
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setSaving(false);
    }
  };

  const dirty = JSON.stringify(toForm(saved)) !== JSON.stringify(form);

  return (
    <Card
      title={t('monthly.title')}
      subtitle={t('monthly.subtitle')}
      actions={(
        <Badge tone={saved.enabled ? 'active' : 'inactive'}>
          {saved.enabled ? t('monthly.status_on') : t('monthly.status_off')}
        </Badge>
      )}
    >
      <Callout tone="info" title={t('monthly.rule_title')}>
        {t('monthly.rule_body', { close: saved.closeTime, departure: saved.departureTime })}
      </Callout>

      {saved.upcoming?.length > 0 && (
        <div className="monthly-upcoming">
          {saved.upcoming.map((u) => (
            <div key={u.month} className="monthly-upcoming__item">
              <div className="monthly-upcoming__date">{formatDate(u.date, t)}</div>
              <div className="monthly-upcoming__meta">
                {u.eventCode
                  ? t('monthly.upcoming_booked', { code: u.eventCode, registered: u.registered ?? 0 })
                  : t('monthly.upcoming_pending')}
              </div>
              <div className="monthly-upcoming__state">
                {u.eventCode ? (
                  <Badge tone={u.bookingOpen ? 'open' : 'inactive'}>
                    {u.bookingOpen ? t('monthly.booking_open') : t('monthly.booking_closed')}
                  </Badge>
                ) : null}
                {u.bookingClosesAt && (
                  <span className="monthly-upcoming__closes">
                    {t(u.bookingReopened ? 'monthly.open_until' : 'monthly.closes_at', { when: formatDateTime(u.bookingClosesAt, locale) })}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSave} noValidate className="monthly-form">
        <label className="checkbox-row">
          <input type="checkbox" checked={form.enabled} onChange={set('enabled')} disabled={!canEdit} />
          {t('monthly.field_enabled')}
        </label>
        <div className="form-row form-row--2">
          <FormField label={t('monthly.field_name')} htmlFor="mp-name" required error={errors.name} hint={t('monthly.field_name_hint')}>
            <Input id="mp-name" value={form.name} onChange={set('name')} disabled={!canEdit} invalid={Boolean(errors.name)} />
          </FormField>
          <FormField label={t('monthly.field_destination')} htmlFor="mp-dest" required error={errors.destination}>
            <Input id="mp-dest" value={form.destination} onChange={set('destination')} disabled={!canEdit} invalid={Boolean(errors.destination)} />
          </FormField>
        </div>
        <div className="form-row form-row--2">
          <FormField label={t('monthly.field_price')} htmlFor="mp-price" required error={errors.price} hint={saved.price != null ? t('monthly.field_price_hint', { price: formatCurrency(Number(saved.price), locale) }) : undefined}>
            <div className="input-suffix">
              <Input id="mp-price" type="number" min={100} step={1} value={form.price} onChange={set('price')} disabled={!canEdit} invalid={Boolean(errors.price)} />
              <span className="input-suffix__unit">{t('common.currency_rwf')}</span>
            </div>
          </FormField>
          <FormField label={t('monthly.field_places')} htmlFor="mp-places" hint={t('monthly.field_places_hint')}>
            <Input id="mp-places" value={t('events.no_limit')} disabled readOnly />
          </FormField>
        </div>
        <div className="form-row form-row--3">
          <FormField label={t('monthly.field_close_time')} htmlFor="mp-close" error={errors.closeTime} hint={t('monthly.field_close_time_hint')}>
            <Input id="mp-close" type="time" value={form.closeTime} onChange={set('closeTime')} disabled={!canEdit} invalid={Boolean(errors.closeTime)} />
          </FormField>
          <FormField label={t('monthly.field_departure_time')} htmlFor="mp-departure" error={errors.departureTime} hint={t('monthly.field_departure_time_hint')}>
            <Input id="mp-departure" type="time" value={form.departureTime} onChange={set('departureTime')} disabled={!canEdit} invalid={Boolean(errors.departureTime)} />
          </FormField>
          <FormField label={t('monthly.field_months_ahead')} htmlFor="mp-months" error={errors.monthsAhead} hint={t('monthly.field_months_ahead_hint')}>
            <Input id="mp-months" type="number" min={0} max={6} step={1} value={form.monthsAhead} onChange={set('monthsAhead')} disabled={!canEdit} invalid={Boolean(errors.monthsAhead)} />
          </FormField>
        </div>
        <FormField label={t('monthly.field_description')} htmlFor="mp-desc" hint={t('monthly.field_description_hint')}>
          <Textarea id="mp-desc" rows={3} value={form.description} onChange={set('description')} disabled={!canEdit} />
        </FormField>
        {canEdit ? (
          <div className="monthly-form__actions">
            {dirty && <Button variant="ghost" onClick={() => { setForm(toForm(saved)); setErrors({}); }} disabled={saving}>{t('common.cancel')}</Button>}
            <Button type="submit" variant="gold" loading={saving} disabled={!dirty}>{t('common.save')}</Button>
          </div>
        ) : (
          <div className="form-hint">{t('monthly.admin_only')}</div>
        )}
      </form>
    </Card>
  );
}
